import { cacheKeys, useWorkspaceAsset, useWorkspaceRoutine } from '@gripp/shared-logic';
import { Button, LoadingIndicator, tokens } from '@gripp/shared-ui';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { RoutinePrintSheet } from './routinePrintSheet';

export const RoutinePrintPage = () => {
  const { t } = useTranslation();
  const { routineId } = useParams<{ routineId: string }>();
  const [searchParams] = useSearchParams();
  const assetId = searchParams.get('assetId') ?? '';

  const fetchedRoutine = useWorkspaceRoutine(routineId ?? '');
  /**
   * `useWorkspaceRoutine` mirrors its query into state through an effect, so
   * navigating between routine ids can render the previous id's routine for a
   * frame under the new URL -- and Print would fire against it. Only trust the
   * hook's value once it actually matches what this page requested.
   */
  const routine = fetchedRoutine?.id === routineId ? fetchedRoutine : undefined;
  const { workspaceAsset, isLoading: isAssetLoading } = useWorkspaceAsset(
    assetId,
    !assetId
  );

  const queryClient = useQueryClient();
  const routineQueryKey = useMemo(
    () => cacheKeys('WorkspaceRoutine').detail(routineId ?? ''),
    [routineId]
  );

  /**
   * Every nested-routine expansion on the sheet is another `WorkspaceRoutine`
   * lookup, and the prefix key counts them all alongside the routine's own.
   * Printing while one is still in flight would produce a sheet with a field
   * missing, so Print waits for the whole page rather than just the routine.
   */
  const routineLookupsInFlight = useIsFetching({
    queryKey: cacheKeys('WorkspaceRoutine').details(),
  });

  /**
   * `useWorkspaceRoutine` mirrors its query into state through an effect, so it
   * trails the cache by one render. Reading the query's own state is what keeps
   * "the lookup came back with nothing" apart from "the value has not been
   * mirrored yet" -- from the hook's `undefined` alone the two are the same,
   * and treating the second as the first flashes the not-found message over a
   * routine that is about to render.
   */
  const routineQueryState = queryClient.getQueryState(routineQueryKey);
  const cachedRoutine = (
    routineQueryState?.data as { getWorkspaceRoutine?: unknown } | undefined
  )?.getWorkspaceRoutine;
  const isRoutineMissing =
    routineQueryState !== undefined &&
    routineQueryState.fetchStatus === 'idle' &&
    routineQueryState.status !== 'pending' &&
    cachedRoutine == null;

  /**
   * Deferred from the moment the sheet actually mounts (routine truthy), not
   * from the moment `routine` is read: on the render that first mounts
   * `RoutinePrintSheet`, its nested `useWorkspaceRoutine` lookups haven't
   * subscribed yet, so `routineLookupsInFlight` still reads zero and a fast
   * click could print a form with a field missing. Waiting a macrotask past
   * that mount lets those subscriptions register before the count is
   * trusted. Keyed on the boolean rather than the `routine` object so a
   * changing object identity can't restart the timer every render.
   */
  const [hasSheetSettled, setHasSheetSettled] = useState(false);
  const hasRoutine = !!routine;
  useEffect(() => {
    if (!hasRoutine) {
      setHasSheetSettled(false);
      return;
    }
    const id = setTimeout(() => setHasSheetSettled(true), 0);
    return () => clearTimeout(id);
  }, [hasRoutine]);

  // A sheet with no assetId has nothing to look up and must stay printable;
  // one that does must wait for the asset's name to arrive for the footer.
  const canPrint =
    hasSheetSettled &&
    !!routine &&
    routineLookupsInFlight === 0 &&
    (!assetId || !isAssetLoading);

  const sheetRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: sheetRef,
    documentTitle: routine?.config?.title || routine?.name,
  });

  if (!routineId) return null;

  return (
    <div style={styles.backdrop}>
      <div style={styles.toolbar}>
        <h1 style={styles.heading}>
          {t('routine.print.previewTitle', 'Print preview')}
        </h1>
        <Button
          state="active"
          size="large"
          onPress={() => handlePrint()}
          disabled={!canPrint}
          testID="routine-print-print-button"
          accessibilityLabel={t('routine.print.button', 'Print')}
        >
          {t('routine.print.button', 'Print')}
        </Button>
      </div>

      {routine ? (
        <div style={styles.sheetShadow}>
          <RoutinePrintSheet
            ref={sheetRef}
            routine={routine}
            asset={workspaceAsset}
          />
        </div>
      ) : isRoutineMissing ? (
        <p style={styles.status}>
          {t('routine.print.notFound', 'This routine could not be loaded.')}
        </p>
      ) : (
        <LoadingIndicator backgroundColor="transparent" />
      )}
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  backdrop: {
    flex: 1,
    overflowY: 'auto',
    // Fixed grey, like the sheet's fixed white: the preview must read the same
    // whichever appearance the app is in.
    backgroundColor: tokens.colors.neutral[300],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacing.lg,
    padding: tokens.spacing['2xl'],
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '8.5in',
    maxWidth: '100%',
  },
  heading: {
    margin: 0,
    fontFamily: tokens.typography.title.medium.fontFamily,
    fontSize: tokens.typography.title.medium.fontSize,
    fontWeight: tokens.typography.title.mediumEmphasised.fontWeight,
    color: tokens.colors.neutral[700],
  },
  sheetShadow: {
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
  },
  status: {
    fontFamily: tokens.typography.body.medium.fontFamily,
    fontSize: tokens.typography.body.medium.fontSize,
    color: '#000000',
  },
};
